import type { UserProfile } from '@/types';

/**
 * Check if a user is authorized to access an event
 * @param user - The current user profile
 * @param event - The event object
 * @returns boolean indicating if user is authorized
 */
export function isUserAuthorizedForEvent(user: UserProfile | null, event: any): boolean {
    if (!user || !event) {
        return false;
    }

    // Event creator/organizer is always authorized
    if (event.userId === user.userId || event.organizer?.id === user.userId) {
        return true;
    }

    // Check if user's email is in the authorized emails list
    if (event.authorizedEmails && Array.isArray(event.authorizedEmails)) {
        const email = String(user.email || '').trim().toLowerCase();
        return event.authorizedEmails.some((e: unknown) => String(e).trim().toLowerCase() === email);
    }

    return false;
}

/**
 * Check if a user can perform check-in operations for an event
 * @param user - The current user profile
 * @param event - The event object
 * @returns boolean indicating if user can check-in
 */
export function canUserCheckInForEvent(user: UserProfile | null, event: any): boolean {
    return isUserAuthorizedForEvent(user, event);
}

/**
 * Check if a user can view event details (always true - events are public)
 * @param user - The current user profile
 * @param event - The event object
 * @returns boolean indicating if user can view details
 */
export function canUserViewEventDetails(user: UserProfile | null, event: any): boolean {
    // Events are always publicly visible
    return true;
}

/**
 * Check if a user can edit/manage an event
 * @param user - The current user profile
 * @param event - The event object
 * @returns boolean indicating if user can edit the event
 */
export function canUserEditEvent(user: UserProfile | null, event: any): boolean {
    return isUserAuthorizedForEvent(user, event);
}
